import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Copy, Play, Calculator, Clock, Package, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import ManufacturingService from '@/api/manufacturing.service';
import { canEdit } from '@/lib/permissions';

const RecipeDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [recipe, setRecipe] = useState<any>(null);

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  useEffect(() => {
    if (id) {
      fetchRecipe();
    }
  }, [id]);

  const fetchRecipe = async () => {
    try {
      setIsLoading(true);
      const response = await ManufacturingService.getRecipe(businessId, parseInt(id!));
      
      if (response.data?.success) {
        setRecipe(response.data.data);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load recipe details",
        });
        navigate('/manufacturing/recipes');
      }
    } catch (error: any) {
      console.error('Error fetching recipe:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load recipe details",
      });
      navigate('/manufacturing/recipes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProductionJob = () => {
    navigate('/manufacturing/production-jobs/create', {
      state: { recipeId: recipe.id, recipeName: recipe.name }
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getDifficultyBadge = (difficulty: string) => {
    const difficultyConfig = {
      easy: { variant: 'default' as const, label: 'Easy' },
      medium: { variant: 'secondary' as const, label: 'Medium' },
      hard: { variant: 'destructive' as const, label: 'Hard' },
      expert: { variant: 'outline' as const, label: 'Expert' }
    };

    const config = difficultyConfig[difficulty as keyof typeof difficultyConfig] || { variant: 'secondary' as const, label: difficulty };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading recipe details...</span>
        </div>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h3 className="text-lg font-semibold">Recipe Not Found</h3>
          <p className="text-muted-foreground">The requested recipe could not be found.</p>
          <Button 
            onClick={() => navigate('/manufacturing/recipes')}
            className="mt-4"
          >
            Back to Recipes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/manufacturing/recipes')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Recipes
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleCreateProductionJob}
            className="flex items-center gap-2"
          >
            <Play className="h-4 w-4" />
            Create Production Job
          </Button>
          {canEdit('Manufacturing') && (
            <Button
              onClick={() => navigate(`/manufacturing/recipes/${recipe.id}/edit`)}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit Recipe
            </Button>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-4 mb-2">
          <h1 className="text-3xl font-bold tracking-tight">{recipe.name}</h1>
          {getDifficultyBadge(recipe.difficulty)}
          <Badge variant="outline">v{recipe.version}</Badge>
        </div>
        <p className="text-muted-foreground">
          {recipe.description} • Code: {recipe.code}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Output</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{recipe.outputQuantity}</div>
            <p className="text-xs text-muted-foreground">{recipe.outputUnit}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Est. Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{recipe.estimatedTime}</div>
            <p className="text-xs text-muted-foreground">minutes</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <Calculator className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(recipe.totalCost || 0)}</div>
            <p className="text-xs text-muted-foreground">material cost</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cost per Unit</CardTitle>
            <Calculator className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(recipe.outputQuantity > 0 ? (recipe.totalCost || 0) / recipe.outputQuantity : 0)}
            </div>
            <p className="text-xs text-muted-foreground">per {recipe.outputUnit}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="bom">Bill of Materials</TabsTrigger>
          <TabsTrigger value="instructions">Instructions</TabsTrigger>
          <TabsTrigger value="quality">Quality Checks</TabsTrigger>
          <TabsTrigger value="usage">Usage History</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Recipe Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Recipe Code</label>
                  <p className="text-sm">{recipe.code}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Category</label>
                  <p className="text-sm capitalize">{recipe.category?.replace('_', ' ')}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Version</label>
                  <p className="text-sm">{recipe.version}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Difficulty</label>
                  <div className="mt-1">{getDifficultyBadge(recipe.difficulty)}</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Production Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Output Quantity</label>
                  <p className="text-sm">{recipe.outputQuantity} {recipe.outputUnit}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Estimated Time</label>
                  <p className="text-sm">{recipe.estimatedTime} minutes</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">BOM Items</label>
                  <p className="text-sm">{recipe.bomItems?.length || 0} items</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created</label>
                  <p className="text-sm">{new Date(recipe.createdAt).toLocaleDateString()}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="bom" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Bill of Materials</CardTitle>
              <CardDescription>Materials required for this recipe</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Unit Cost</TableHead>
                    <TableHead>Total Cost</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recipe.bomItems?.map((item: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.itemName}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.unit}</TableCell>
                      <TableCell>{formatCurrency(item.cost)}</TableCell>
                      <TableCell>{formatCurrency(item.quantity * item.cost)}</TableCell>
                      <TableCell>{item.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-t-2">
                    <TableCell colSpan={4} className="font-bold">Total Material Cost</TableCell>
                    <TableCell className="font-bold">{formatCurrency(recipe.totalCost || 0)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="instructions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Manufacturing Instructions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="whitespace-pre-wrap text-sm">
                {recipe.instructions || 'No instructions provided.'}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quality" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Quality Control Checks</CardTitle>
            </CardHeader>
            <CardContent>
              {recipe.qualityChecks && recipe.qualityChecks.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Parameter</TableHead>
                      <TableHead>Specification</TableHead>
                      <TableHead>Tolerance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recipe.qualityChecks.map((check: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{check.parameter}</TableCell>
                        <TableCell>{check.specification}</TableCell>
                        <TableCell>{check.tolerance}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground">No quality checks defined.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="usage" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Usage History</CardTitle>
              <CardDescription>Recent production jobs using this recipe</CardDescription>
            </CardHeader>
            <CardContent>
              {recipe.usageHistory && recipe.usageHistory.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job Number</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recipe.usageHistory.map((usage: any) => (
                      <TableRow key={usage.id}>
                        <TableCell className="font-medium">{usage.jobNumber}</TableCell>
                        <TableCell>{usage.quantity}</TableCell>
                        <TableCell>
                          <Badge variant={usage.status === 'completed' ? 'default' : 'secondary'}>
                            {usage.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(usage.date).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground">No production history available.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RecipeDetail; 